<template>
  <div class="border-t border-gray-100 first:border-t-0" role="rowgroup" :data-po-row="row.poNumber">
    <div :class="[GRID_COLUMNS, rowBackground]" class="grid items-center gap-3 px-4 py-2.5 text-sm" role="row">
      <span role="cell">
        <button
          type="button"
          class="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
          :aria-expanded="expanded"
          :aria-label="`${expanded ? 'Collapse' : 'Expand'} ${row.poNumber}`"
          @click="emit('toggle')"
        >
          <v-icon size="18" icon="mdi-chevron-right" class="transition-transform duration-150 ease-out motion-reduce:transition-none" :class="expanded ? 'rotate-90' : ''" />
        </button>
      </span>
      <span class="flex items-center gap-2 border-l-4 pl-2" :style="{ borderColor: color }" role="cell">
        <button type="button" class="font-semibold text-gray-900 hover:text-blue-700 hover:underline" :aria-expanded="expanded" @click="emit('toggle')">{{ row.poNumber }}</button>
        <span v-if="showNew" class="badge bg-blue-600 text-white">New</span>
      </span>
      <span role="cell"><StatusPill :status="row.status" /></span>
      <span role="cell" class="tabular-nums" :class="shipDates === '—' ? 'text-gray-500' : 'text-gray-700'">{{ shipDates }}</span>
      <template v-if="row.linesSyncing">
        <span class="text-right text-gray-500" role="cell">Syncing…</span>
        <span class="text-right text-gray-500" role="cell">—</span>
      </template>
      <template v-else>
        <span class="text-right tabular-nums" role="cell">
          {{ fmtPcs(row.shipped) }}
          <span class="text-gray-500">/ {{ fmtPcs(row.ordered) }}</span>
        </span>
        <span class="text-right tabular-nums" role="cell" :class="row.toPlan > 0 ? 'text-gray-700' : 'text-gray-500'">{{ row.toPlan > 0 ? fmtPcs(row.toPlan) : '—' }}</span>
      </template>
      <span v-if="row.attention" class="font-semibold" role="cell" :class="ATTENTION_TONE_TEXT[attentionTone(row.attention.kind)]">
        {{ attentionText(row.attention) }}
      </span>
      <span v-else class="text-gray-500" role="cell">—</span>
    </div>
    <div v-if="expanded" class="sticky left-0 bg-gray-50 px-6 pb-4 pt-1 text-sm" :style="{ width: 'var(--panel-w, 100%)' }" role="row">
      <div role="cell" :aria-colspan="COLUMN_COUNT">
        <PoTrackerDetail
          :po-number="row.poNumber"
          @mark-shipped="emit('mark-shipped', $event)"
          @shipping-details="emit('shipping-details', $event)"
          @request-delivery="emit('request-delivery', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import PoTrackerDetail from '@/components/poTracker/PoTrackerDetail.vue';
  import StatusPill from '@/components/poTracker/StatusPill.vue';
  import { COLUMN_COUNT, GRID_COLUMNS } from '@/components/poTracker/poTrackerGrid';
  import { ATTENTION_TONE_TEXT } from '@/components/poTracker/poTrackerStyles';
  import type { PoTrackerRow, Shipment, ShippingEvent } from '@/types/poTracker';
  import { attentionText, attentionTone, fmtPcs, shipDatesText } from '@/utils/poTracker';

  const props = defineProps<{ row: PoTrackerRow; color: string; expanded: boolean; showNew: boolean }>();
  const emit = defineEmits<{
    toggle: [];
    'mark-shipped': [shipment: Shipment];
    'shipping-details': [event: ShippingEvent];
    'request-delivery': [poNumber: string];
  }>();

  const shipDates = computed(() => shipDatesText(props.row));
  // An open PO reads as open; the New tint is for closed rows only.
  const rowBackground = computed(() => {
    if (props.expanded) return 'bg-gray-50';
    return props.showNew ? 'bg-blue-50/60' : '';
  });
</script>
